import { AsyncPipe, JsonPipe } from '@angular/common';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatRipple } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, map, of, startWith } from 'rxjs';
import { SfoUiJSONSchema7 } from '../../../metadata.model';
import { SfoRichTextInputComponent } from '../../../widgets/rich-text/rich-text.component';
import { DateVariableInputComponent } from '../../csl/date-variable-input/date-variable-input.component';
import { SchemaKeywords, SfoUiWidgetType } from '../../types';
import { FormControlPipe } from '../form-control.pipe';
import { HandleSchemaTypePipe } from '../handle-schema-type.pipe';
import { HasEnumPipe } from '../has-enum.pipe';
import { BooleanInputComponent } from './boolean-input/boolean-input.component';
import { FontSizeCssInputComponent } from './font-size-css-input/font-size-css-input.component';
import { DateInputComponent } from './date-input/date-input.component';
import { EnumInputComponent } from './enum-input/enum-input.component';
import { NumberInputComponent } from './number-input/number-input.component';
import { RadioGroupInputComponent } from './radio-group-input/radio-group-input.component';
import { TextareaInputComponent } from './textarea/textarea.component';

@Component({
  selector: 'sfo-dynamic-input',
  templateUrl: './dynamic-input.component.html',
  styleUrls: ['./dynamic-input.component.scss'],
  imports: [
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatTooltipModule,
    DateInputComponent,
    DateVariableInputComponent,
    SfoRichTextInputComponent,
    FormsModule,
    ReactiveFormsModule,
    TextareaInputComponent,
    RadioGroupInputComponent,
    EnumInputComponent,
    BooleanInputComponent,
    FontSizeCssInputComponent,
    NumberInputComponent,
    AsyncPipe,
    JsonPipe,
    HasEnumPipe,
    HandleSchemaTypePipe,
    FormControlPipe
  ],
})
export class DynamicInputComponent implements OnInit {
  @Input() schema!: SfoUiJSONSchema7;
  @Input() aFormControl!: FormControl;
  @Input() defaultValue: unknown;
  @ViewChild(MatRipple) ripple: MatRipple;

  hasStringExamplesAutocomplete = false;
  filteredStringExampleOptions$: Observable<Array<{ value: string; label?: string }>> = of([]);

  private userEvent: boolean = false;

  SchemaKeywords = SchemaKeywords;
  SfoUiWidgetType = SfoUiWidgetType;

  ngOnInit(): void {
    if (!this.aFormControl) {
      console.warn('DynamicInputComponent missing FormControl for schema', this.schema?.title);
      return;
    }

    this.initStringExamplesAutocomplete();

    this.aFormControl.valueChanges
      .pipe(
        map(() => {
          if (!this.userEvent) {
            this.ripple?.launch({
              centered: true,
              animation: { enterDuration: 500, exitDuration: 0 },
            });
          }
        }),
      )
      .subscribe();

    this.initValueForZeroMinLength();
  }

  onFocus() {
    this.userEvent = true;
  }

  onBlur() {
    this.userEvent = false;
  }

  resetToDefault() {
    this.aFormControl.markAsDirty();
    if (this.schema && this.aFormControl) {
      this.aFormControl.setValue(this.defaultValue || this.schema.default);
    }
  }

  private initValueForZeroMinLength() {
    if (this.schema?.minLength === 0 && this.aFormControl.value === null) {
      this.aFormControl.setValue(this.schema.default || this.defaultValue);
    }
  }

  private initStringExamplesAutocomplete(): void {
    if (!this.schema || this.schema.type !== 'string') {
      return;
    }

    const options = (this.schema.examples ?? [])
      .map((ex): { value: string; label?: string } | null => {
        if (typeof ex === 'string') {
          const value = ex.trim();
          return value ? { value } : null;
        }

        if (typeof ex === 'object' && ex !== null) {
          const maybeValue = (ex as any).value;
          const value = typeof maybeValue === 'string' ? maybeValue.trim() : '';
          if (!value) {
            return null;
          }
          const label = typeof (ex as any).label === 'string' ? (ex as any).label : undefined;
          return { value, label };
        }

        return null;
      })
      .filter(Boolean) as Array<{ value: string; label?: string }>;

    if (!options.length) {
      return;
    }

    const uniqueOptions = Array.from(
      new Map(options.map((o) => [o.value, o])).values(),
    );
    this.hasStringExamplesAutocomplete = true;
    this.filteredStringExampleOptions$ = this.aFormControl.valueChanges.pipe(
      startWith(this.aFormControl.value ?? ''),
      map((value) => {
        const raw = (value ?? '').toString().toLowerCase();
        if (!raw) {
          return uniqueOptions;
        }
        return uniqueOptions.filter((option) => {
          const valueMatch = option.value.toLowerCase().includes(raw);
          const labelMatch = option.label?.toLowerCase().includes(raw) ?? false;
          return valueMatch || labelMatch;
        });
      }),
    );
  }
}
